// The generated form of a calculator: one field per declared input (prayog_properties.core.inputs), advanced inputs
// folded away.
import { Checkbox, Collapse, Group, MultiSelect, NumberInput, Select, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { useState } from "react";
import type { StoredFile } from "../../lib/api";
import { parsePath, pathText, type CalcDesc, type InputSchema } from "../../lib/properties";

function Num({ label, value, onChange, s, w }: { label: string; value: number; onChange: (v: number) => void; s: Partial<InputSchema>; w?: number }) {
  return <NumberInput size="xs" label={label} w={w} value={value} min={s.min ?? undefined} max={s.max ?? undefined}
    allowDecimal={!s.integer} decimalScale={s.integer ? 0 : 8}
    rightSection={s.unit ? <Text size="xs" c="dimmed">{s.unit}</Text> : undefined} rightSectionWidth={s.unit ? 44 : undefined}
    onChange={(v) => onChange(typeof v === "number" ? v : Number(v) || 0)} />;
}

const BAND_WORDS = [{ value: "all", label: "all bands" }, { value: "occupied", label: "occupied bands" },
  { value: "edges", label: "top valence + bottom conduction" }, { value: "list", label: "band indices…" }];

export function InputField({ name, s, value, onChange, files }: {
  name: string; s: InputSchema; value: any; onChange: (v: unknown) => void; files: StoredFile[];
}) {
  const [text, setText] = useState<string>(() => (s.kind === "kpath" ? pathText(value) : s.kind === "number_list" ? (value ?? []).join(", ")
    : s.kind === "bands" && Array.isArray(value) ? value.join(", ") : ""));
  const [err, setErr] = useState("");
  const help = s.help ? <Text size="xs" c="dimmed">{s.help}</Text> : null;
  switch (s.kind) {
    case "number":
      return <Stack gap={2}><Num label={s.label} value={value} onChange={onChange} s={s} />{help}</Stack>;
    case "bool":
      return <Checkbox size="xs" label={s.label} checked={!!value} onChange={(e) => onChange(e.currentTarget.checked)} />;
    case "choice":
      return <Select size="xs" label={s.label} data={s.options ?? []} value={value} allowDeselect={false} onChange={(v) => onChange(v)} />;
    case "mesh":
      return (
        <Group grow gap="xs" align="flex-end">
          {[0, 1, 2].map((a) => <Num key={a} label={a === 0 ? s.label : " "} s={{ integer: true, min: 1 }} value={value[a]}
            onChange={(v) => onChange(value.map((x: number, j: number) => (j === a ? v : x)))} />)}
        </Group>
      );
    case "vector":
      return (
        <Group grow gap="xs" align="flex-end">
          {[0, 1, 2].map((a) => <Num key={a} label={a === 0 ? s.label : " "} s={{ unit: s.unit }} value={value[a]}
            onChange={(v) => onChange(value.map((x: number, j: number) => (j === a ? v : x)))} />)}
        </Group>
      );
    case "energy_grid":
      return (
        <Group grow gap="xs" align="flex-end">
          <Num label={`${s.label}: from`} s={{ unit: "eV" }} value={value.min} onChange={(v) => onChange({ ...value, min: v })} />
          <Num label="to" s={{ unit: "eV" }} value={value.max} onChange={(v) => onChange({ ...value, max: v })} />
          <Num label="points" s={{ integer: true, min: 1 }} value={value.n} onChange={(v) => onChange({ ...value, n: v })} />
        </Group>
      );
    case "number_list":
      return (
        <TextInput size="xs" label={`${s.label}${s.unit ? ` (${s.unit})` : ""}`} value={text} error={err || undefined}
          onChange={(e) => {
            const t = e.currentTarget.value;
            setText(t);
            const v = t.split(/[\s,;]+/).filter(Boolean).map(Number);
            if (v.length && v.every(Number.isFinite)) { setErr(""); onChange(v); } else setErr("numbers separated by commas");
          }} />
      );
    case "bands": {
      const mode = Array.isArray(value) ? "list" : value;
      return (
        <Stack gap={4}>
          <Select size="xs" label={s.label} data={BAND_WORDS} value={mode} allowDeselect={false}
            onChange={(v) => onChange(v === "list" ? [0] : v)} />
          {mode === "list" && <TextInput size="xs" placeholder="e.g. 2, 3, 4" value={text} error={err || undefined}
            onChange={(e) => {
              const t = e.currentTarget.value;
              setText(t);
              const v = t.split(/[\s,;]+/).filter(Boolean).map(Number);
              if (v.length && v.every((x) => Number.isInteger(x) && x >= 0)) { setErr(""); onChange(v); } else setErr("band indices from 0");
            }} />}
        </Stack>
      );
    }
    case "kpath":
      return (
        <Stack gap={2}>
          <TextInput size="xs" label={s.label} placeholder="automatic (the fit's DFT path, or the standard path)" value={text}
            error={err || undefined} onChange={(e) => {
              const t = e.currentTarget.value;
              setText(t);
              try { onChange(parsePath(t)); setErr(""); } catch (x) { setErr((x as Error).message); }
            }} />
          <Text size="xs" c="dimmed">Points as "Γ 0 0 0; M 1/2 0 0; K 1/3 1/3 0" (fractional k); "|" for a jump.</Text>
        </Stack>
      );
    case "files": {
      const models = files.filter((f) => f.status === "ready" && /\.json(\.gz)?$/i.test(f.name));
      return <MultiSelect size="xs" label={s.label} data={models.map((f) => ({ value: f.id, label: f.name }))} value={value ?? []}
        onChange={onChange} placeholder={models.length ? "choose model files of this project" : "upload model files to this project first"}
        data-testid={`input-${name}`} />;
    }
    default:
      return null;
  }
}

export function PropertyForm({ calc, values, onChange, files }: {
  calc: CalcDesc; values: Record<string, unknown>; onChange: (v: Record<string, unknown>) => void; files: StoredFile[];
}) {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(calc.inputs);
  const field = ([k, s]: [string, InputSchema]) => (
    <InputField key={`${calc.id}.${k}`} name={k} s={s} value={values[k] ?? s.default} files={files}
      onChange={(v) => onChange({ ...values, [k]: v })} />
  );
  const adv = entries.filter(([, s]) => s.advanced);
  return (
    <Stack gap="xs" data-testid="property-form">
      {entries.filter(([, s]) => !s.advanced).map(field)}
      {adv.length > 0 && (
        <>
          <UnstyledButton onClick={() => setOpen((o) => !o)}><Text size="xs" c="blue">{open ? "Hide" : "Show"} advanced inputs ({adv.length})</Text></UnstyledButton>
          <Collapse in={open}><Stack gap="xs">{adv.map(field)}</Stack></Collapse>
        </>
      )}
    </Stack>
  );
}
